import * as React from "react";

export type StatCardTone = "success" | "warning" | "destructive" | "muted";

export interface StatCardProps {
  label: React.ReactNode;
  value: string | number;
  hint?: React.ReactNode;
  tone?: StatCardTone;
  /** When true, value is replaced with an animated skeleton. value/tone are ignored. */
  loading?: boolean;
  className?: string;
}

const TONE_CLASSES: Record<StatCardTone, string> = {
  success: "text-success",
  warning: "text-warning",
  destructive: "text-destructive",
  muted: "text-muted-foreground",
};

export function StatCard({
  label,
  value,
  hint,
  tone,
  loading = false,
  className,
}: StatCardProps) {
  const valueClasses = [
    // tabular-nums keeps digit columns aligned across cards; truncate
    // prevents long numbers ("1,234,567") from breaking mid-digit on
    // narrow viewports — the card shrinks but the value stays one line
    // and shows an ellipsis when it can't fit (rare).
    "text-2xl font-semibold tabular-nums truncate",
    tone ? TONE_CLASSES[tone] : "text-foreground",
  ].join(" ");
  const wrapperClasses = [
    "rounded-lg border border-border bg-card px-4 py-3 shadow-sm",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={wrapperClasses}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      {loading ? (
        <div
          className="h-7 w-20 animate-pulse rounded bg-muted"
          aria-hidden="true"
        />
      ) : (
        <div className={valueClasses}>{value}</div>
      )}
      {hint && (
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}
